import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchBilling, fetchUsage } from '../services/api';
import { useAppStore } from '../store/useAppStore';
import Icon from '../components/Icon';
import Button from '../components/Button';
import Chip from '../components/Chip';

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export const PLAN_META: Record<string, { label: string; price: string; popular?: boolean }> = {
  free: { label: 'Free', price: 'Free' },
  plus: { label: 'Plus', price: '$9.99/month', popular: true },
  pro: { label: 'Pro', price: '$24.99/month' },
  enterprise: { label: 'Enterprise', price: 'Custom' },
};

interface BillingData {
  plan: {
    account_mode: string;
    current_plan_id: string;
    seat_count: number;
    pooled_quota_bytes: string;
    extra_storage_bytes: string;
  } | null;
  usage: { used_bytes: number; quota_bytes: number; file_count: number } | null;
  invoices: Array<{
    id: string;
    number: string;
    amount_cents: number;
    currency: string;
    status: 'paid' | 'open' | 'void';
    date: string;
    pdf_url?: string;
  }>;
  manage_url: string | null;
}

const STATUS_TONE: Record<string, 'success' | 'warn' | 'neutral'> = {
  paid: 'success',
  open: 'warn',
  void: 'neutral',
};

export default function Billing() {
  const { email: userEmail, displayName: userName } = useAppStore();

  const { data, isLoading, isError } = useQuery<BillingData>({
    queryKey: ['billing'],
    queryFn: fetchBilling,
  });

  const { data: usage } = useQuery({
    queryKey: ['usage'],
    queryFn: fetchUsage,
  });

  if (isError) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <Icon name="warn" size={28} color="var(--danger)" />
        <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>Unable to load billing information</div>
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Please try again or visit your Softknack account.</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ flex: 1, padding: 24 }}>
        <div className="t-skeleton" style={{ width: 200, height: 28, borderRadius: 6, marginBottom: 8 }} />
        <div className="t-skeleton" style={{ width: 320, height: 18, borderRadius: 6, marginBottom: 24 }} />
        <div style={{ display: 'flex', gap: 16, maxWidth: 960 }}>
          <div className="t-skeleton" style={{ flex: 1.4, height: 280, borderRadius: 12 }} />
          <div className="t-skeleton" style={{ flex: 1, height: 280, borderRadius: 12 }} />
        </div>
      </div>
    );
  }

  const planId = data?.plan?.current_plan_id ?? 'free';
  const planMeta = PLAN_META[planId] ?? { label: planId, price: '—' };

  const usedBytes = usage?.used_bytes ?? data?.usage?.used_bytes ?? 0;
  const quotaBytes = usage?.quota_bytes ?? (data?.plan?.pooled_quota_bytes ? Number(data.plan.pooled_quota_bytes) : 50 * 1024 * 1024 * 1024);
  const pct = quotaBytes > 0 ? Math.min(100, (usedBytes / quotaBytes) * 100) : 0;
  const danger = pct > 90;

  const invoices = data?.invoices ?? [];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
      {/* Header */}
      <div style={{ padding: '24px 24px 0' }}>
        <h2 style={{ fontSize: 'var(--fs-2xl)', fontWeight: 700 }}>Plan &amp; billing</h2>
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', margin: '4px 0 0' }}>
          Your subscription is managed through your Softknack account.
        </p>
      </div>

      <div style={{ flex: 1, padding: 24 }}>
        <div style={{ maxWidth: 960 }}>

          {/* Two-column cards */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'stretch', flexWrap: 'wrap' }}>

            {/* Left — Current plan */}
            <div style={{ flex: '1.4 1 480px', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--r-4)', padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 'var(--fs-xl)', fontWeight: 700 }}>{planMeta.label}</span>
                    {planMeta.popular && <Chip tone="info">Popular</Chip>}
                  </div>
                  <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', marginTop: 6 }}>
                    {planId !== 'free' ? `${planMeta.price} · renews automatically` : planMeta.price}
                  </div>
                </div>
                {planId !== 'free' && (
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 'var(--fs-2xl)', fontWeight: 700 }}>{planMeta.price.split('/')[0]}</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>per month</div>
                  </div>
                )}
              </div>

              <div style={{ height: 1, background: 'var(--line)' }} />

              {/* Storage bar */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Storage used</span>
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
                    {formatBytes(usedBytes)} of {formatBytes(quotaBytes)}
                  </span>
                </div>
                <div style={{ height: 6, background: 'var(--paper-3)', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${pct}%`,
                    background: danger ? 'var(--danger)' : 'var(--accent)',
                    borderRadius: 999,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Button kind="accent" icon="sparkle">Upgrade</Button>
                {planId !== 'free' && (
                  <button style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 'var(--fs-sm)', color: 'var(--ink-3)',
                    textDecoration: 'underline',
                  }}>
                    Cancel subscription
                  </button>
                )}
              </div>
            </div>

            {/* Right — Billing & licence */}
            <div style={{ flex: '1 1 320px', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--r-4)', padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ fontSize: 'var(--fs-md)', fontWeight: 700, marginBottom: 2 }}>Billing &amp; licence</div>
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Handled by Softknack</div>
              </div>

              {/* Info note */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <Icon name="shield-check" size={16} color="var(--success)" style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', lineHeight: 1.5 }}>
                  Authentication, licensing and payment are managed centrally by the Softknack platform.
                </span>
              </div>

              {/* Account row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--paper-2)', borderRadius: 'var(--r-3)' }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 'var(--r-2)', background: 'var(--card)', border: '1px solid var(--line)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)', flexShrink: 0,
                }}>
                  <Icon name="user" size={15} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userEmail || userName}</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>Softknack account</div>
                </div>
              </div>

              {/* Manage button */}
              <a
                href={data?.manage_url ?? 'https://app.softknack.com/billing'}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '9px 0',
                  background: 'var(--card)', border: '1px solid var(--line-strong)',
                  borderRadius: 'var(--r-2)', fontSize: 'var(--fs-sm)', fontWeight: 600,
                  color: 'var(--ink)', textDecoration: 'none', cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--paper-2)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--card)')}
              >
                <Icon name="external" size={13} />
                Manage on Softknack
              </a>
            </div>
          </div>

          {/* Recent charges */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--r-4)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 'var(--fs-md)', fontWeight: 700 }}>Recent charges</div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>Read-only · invoices live on the platform</div>
            </div>

            {invoices.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', borderTop: '1px solid var(--line)' }}>
                <Icon name="file" size={28} color="var(--ink-4)" />
                <div style={{ marginTop: 12, fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>No invoices yet</div>
                <div style={{ marginTop: 4, fontSize: 'var(--fs-xs)', color: 'var(--ink-4)' }}>Invoices will appear here once your first charge is processed on Softknack.</div>
              </div>
            ) : (
              <table className="t-table" style={{ fontSize: 'var(--fs-md)' }}>
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => {
                    const amount = (inv.amount_cents / 100).toFixed(2);
                    const currency = (inv.currency ?? 'USD').toUpperCase();
                    return (
                      <tr key={inv.id}>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>{inv.number}</td>
                        <td style={{ color: 'var(--ink-2)' }}>{fmtDate(inv.date)}</td>
                        <td>{currency === 'USD' ? '$' : currency + ' '}{amount}</td>
                        <td><Chip tone={STATUS_TONE[inv.status] ?? 'neutral'} dot>{inv.status[0].toUpperCase() + inv.status.slice(1)}</Chip></td>
                        <td>
                          {inv.pdf_url ? (
                            <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer"
                              style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', textDecoration: 'none' }}>
                              <Icon name="download" size={13} /> PDF
                            </a>
                          ) : <span style={{ color: 'var(--ink-4)' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
